/* ============================================================
   Background Video 310 — Carousel controller
   Vanilla JS. Handles arrows, dots, swipe, video pause-on-change,
   and lazy-loading iframes (YouTube/Vimeo) on first activation.
   ============================================================ */

(function () {
  'use strict';

  class HeroCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-carousel-track]');
      this.slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
      this.prevBtn = root.querySelector('[data-carousel-prev]');
      this.nextBtn = root.querySelector('[data-carousel-next]');
      this.dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));

      this.count = this.slides.length;
      this.index = 0;
      this.loop = root.dataset.loop === 'true';

      this.dragging = false;
      this.dragStartX = 0;
      this.dragDx = 0;

      if (this.count <= 1) {
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
      }

      this.bind();
      this.activate(0, { skipAnimation: true });
    }

    bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = parseInt(dot.dataset.carouselDot, 10);
          if (!Number.isNaN(i)) this.go(i);
        });
      });

      // Keyboard
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { this.go(this.index - 1); }
        if (e.key === 'ArrowRight') { this.go(this.index + 1); }
      });

      // Touch / pointer swipe
      this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.track.addEventListener('pointerup',   this.onPointerUp.bind(this));
      this.track.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }

    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.dragging = true;
      this.dragStartX = e.clientX;
      this.dragDx = 0;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(e.pointerId);
    }

    onPointerMove(e) {
      if (!this.dragging) return;
      this.dragDx = e.clientX - this.dragStartX;
      const slideW = this.track.getBoundingClientRect().width;
      const offset = (this.index * 100) - (this.dragDx / slideW * 100);
      this.track.style.transform = `translate3d(${-offset}%, 0, 0)`;
    }

    onPointerUp(e) {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.classList.remove('is-dragging');
      try { this.track.releasePointerCapture(e.pointerId); } catch (_) {}

      const slideW = this.track.getBoundingClientRect().width;
      const threshold = Math.min(80, slideW * 0.15);

      if (this.dragDx > threshold) {
        this.go(this.index - 1);
      } else if (this.dragDx < -threshold) {
        this.go(this.index + 1);
      } else {
        this.activate(this.index);
      }
      this.dragDx = 0;
    }

    go(target) {
      let next = target;
      if (this.loop) {
        next = ((target % this.count) + this.count) % this.count;
      } else {
        next = Math.max(0, Math.min(this.count - 1, target));
      }
      if (next === this.index && this.dragDx === 0) {
        this.activate(this.index);
        return;
      }
      this.activate(next);
    }

    activate(i, opts = {}) {
      const prev = this.index;
      this.index = i;

      if (opts.skipAnimation) {
        this.track.style.transition = 'none';
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }
      this.track.style.transform = `translate3d(${-i * 100}%, 0, 0)`;

      this.slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === i);
      });

      this.dots.forEach((dot, idx) => {
        const active = idx === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.pauseAllVideosExcept(i);
      this.activateSlideMedia(i);
      if (prev !== i) this.deactivateSlideMedia(prev);
    }

    pauseAllVideosExcept(activeIndex) {
      this.slides.forEach((slide, idx) => {
        if (idx === activeIndex) return;

        // Native videos
        slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
          try { v.pause(); v.currentTime = 0; } catch (_) {}
        });

        // YouTube / Vimeo iframes — postMessage pause
        slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
          this.pauseEmbed(iframe);
        });
      });
    }

    pauseEmbed(iframe) {
      const provider = iframe.dataset.provider;
      if (!iframe.contentWindow) return;
      try {
        if (provider === 'youtube') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
        } else if (provider === 'vimeo') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ method: 'pause' }),
            '*'
          );
        }
      } catch (_) {}
    }

    activateSlideMedia(i) {
      const slide = this.slides[i];
      if (!slide) return;

      // Lazy-load iframe src on first activation
      slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
        if (!iframe.getAttribute('src') && iframe.dataset.embedBase) {
          const base = iframe.dataset.embedBase;
          const sep = base.includes('?') ? '&' : '?';
          iframe.setAttribute('src', `${base}${sep}autoplay=1`);
        } else {
          this.playEmbed(iframe);
        }
      });

      // Autoplay native videos on the active slide
      slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
        v.preload = 'auto';
        const playPromise = v.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    deactivateSlideMedia(i) {
      const slide = this.slides[i];
      if (!slide) return;
      slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
        try { v.pause(); } catch (_) {}
      });
      slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
        this.pauseEmbed(iframe);
      });
    }

    playEmbed(iframe) {
      const provider = iframe.dataset.provider;
      if (!iframe.contentWindow) return;
      try {
        if (provider === 'youtube') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        } else if (provider === 'vimeo') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ method: 'play' }),
            '*'
          );
        }
      } catch (_) {}
    }
  }

  function init() {
    document.querySelectorAll('[data-hero-carousel]').forEach((el) => {
      if (el.__heroCarouselReady) return;
      el.__heroCarouselReady = true;
      new HeroCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('[data-carousel-slide]');
    if (!slide) return;
    const root = slide.closest('[data-hero-carousel]');
    const idx = parseInt(slide.dataset.index, 10);
    if (root && root.__heroCarouselInstance) {
      root.__heroCarouselInstance.go(idx);
    } else if (root) {
      // Fallback: instantiate fresh and jump
      const inst = new HeroCarousel(root);
      root.__heroCarouselInstance = inst;
      if (!Number.isNaN(idx)) inst.go(idx);
    }
  });
})();